class PasswordRequirements {

    /**
     * @since 2.0.0
     * @method constructor
     */
    constructor() {
        // Set up variables
        this.selector         = '#reg_password';
        this.fieldSelector    = '.woocommerce-form-row, .form-row, .form-field, .field';
        this.strengthSelector = '.woocommerce-password-strength';
        this.statusSelector   = '[data-password-requirements-status]';
        this.observerData     = 'password-requirements-observer';
        this.minimumLength    = 12;
        this.updateDelay      = 100;
        this.invalidClass     = 'is-invalid';
        this.validClass       = 'is-valid';
        this.strongClass      = 'strong';

        // Run initialization
        this.init()
    }

    /**
     * Initialize the class.
     *
     * @method init
     * @since 2.0.0
     * @return void
     */
    init() {
        // Search for our password fields
        $(this.selector).not('.init').each((i, input) => {

            // Declare and initialize
            let password = $(input);
            let field    = password.closest(this.fieldSelector);

            // Prevent double initialization
            password.addClass('init');

            // Fall back to the immediate parent
            if (!field.length)
                field = password.parent();

            // Locate the requirements status
            let status = field.find(this.statusSelector).first();

            // Create the status when it does not exist
            if (!status.length)
                status = this.createStatus(password, field);

            // Associate the status with the password field
            this.setAccessibilityAttributes(password, status);

            // Watch the WooCommerce strength meter
            this.observeStrengthMeter(password, field, status);

            // Listen for password changes
            password.on(
                'input.passwordRequirements ' +
                'change.passwordRequirements ' +
                'blur.passwordRequirements',
                () => {

                    /*
                     * WooCommerce may create or update the strength meter
                     * after the password input event.
                     */
                    this.observeStrengthMeter(password, field, status);

                    // Let WooCommerce update before checking the result
                    window.setTimeout(() => {
                        this.validate(password, field, status);
                    }, this.updateDelay);

                }
            );

            // Account for an autofilled password
            window.setTimeout(() => {
                this.observeStrengthMeter(password, field, status);
                this.validate(password, field, status);
            }, this.updateDelay);

        });
    }

    /**
     * Create the password requirements status element.
     *
     * @method createStatus
     * @since 2.0.0
     * @param {jQuery} password Password input.
     * @param {jQuery} field Password field container.
     * @return {jQuery}
     */
    createStatus(password, field) {
        // Create the status element
        let status = $('<div>', {
            id:                                  this.getStatusId(password),
            class:                               'password-requirements-status',
            'data-password-requirements-status': '',
            'aria-live':                         'polite',
            hidden:                              true
        });

        // Locate the WooCommerce strength meter
        let strength = this.getStrengthMeter(field);

        // Place the status beneath the strength meter
        if (!!strength.length) {
            strength.after(status);

            return status
        }

        // Fall back to placing it after the password input
        password.after(status);

        return status
    }

    /**
     * Locate the WooCommerce strength meter.
     *
     * @method getStrengthMeter
     * @since 2.0.0
     * @param {jQuery} field Password field container.
     * @return {jQuery}
     */
    getStrengthMeter(field) {
        // Locate the meter within the password field
        let strength = field.find(this.strengthSelector).first();

        // Fall back to the containing form
        if (!strength.length)
            strength = field.closest('form')
                .find(this.strengthSelector)
                .first();

        return strength
    }

    /**
     * Watch the WooCommerce strength meter for changes.
     *
     * @method observeStrengthMeter
     * @since 2.0.0
     * @param {jQuery} password Password input.
     * @param {jQuery} field Password field container.
     * @param {jQuery} status Requirements status.
     * @return void
     */
    observeStrengthMeter(password, field, status) {
        // Locate the WooCommerce strength meter
        let strength = this.getStrengthMeter(field);

        // Bail if the strength meter is unavailable
        if (!strength.length)
            return;

        // Move the status beneath the meter if necessary
        if (!status.prev().is(strength))
            strength.after(status);

        // Prevent double initialization
        if (!!strength.data(this.observerData))
            return;

        // Create the observer
        let observer = new MutationObserver(() => {
            this.validate(password, field, status);
        });

        // Watch for strength changes
        observer.observe(strength.get(0), {
            attributes:      true,
            childList:       true,
            characterData:   true,
            subtree:         true,
            attributeFilter: [
                'class'
            ]
        });

        // Store the observer
        strength.data(this.observerData, observer);
    }

    /**
     * Set the password field accessibility attributes.
     *
     * @method setAccessibilityAttributes
     * @since 2.0.0
     * @param {jQuery} password Password input.
     * @param {jQuery} status Requirements status.
     * @return void
     */
    setAccessibilityAttributes(password, status) {
        // Ensure the status has an ID
        if (!status.attr('id'))
            status.attr('id', this.getStatusId(password));

        // Declare and initialize
        let statusId    = status.attr('id');
        let describedBy = (
            password.attr('aria-describedby') || ''
        ).split(/\s+/).filter(Boolean);

        // Add our status ID
        if (-1 === describedBy.indexOf(statusId))
            describedBy.push(statusId);

        // Update the password field
        password.attr(
            'aria-describedby',
            describedBy.join(' ')
        );
    }

    /**
     * Get the requirements status element ID.
     *
     * @method getStatusId
     * @since 2.0.0
     * @param {jQuery} password Password input.
     * @return {string}
     */
    getStatusId(password) {
        // Get the password input ID
        let passwordId = password.attr('id');

        // Use the password ID when available
        if (!!passwordId)
            return passwordId + '-requirements-status';

        return 'password-requirements-status'
    }

    /**
     * Determine whether WooCommerce considers the password strong.
     *
     * @method isStrong
     * @since 2.0.0
     * @param {jQuery} field Password field container.
     * @return {boolean}
     */
    isStrong(field) {
        // Locate the WooCommerce strength meter
        let strength = this.getStrengthMeter(field);

        // Bail if the strength meter is unavailable
        if (!strength.length)
            return false;

        return strength.hasClass(this.strongClass)
    }

    /**
     * Determine whether the password meets all requirements.
     *
     * @method meetsRequirements
     * @since 2.0.0
     * @param {string} value Password value.
     * @param {jQuery} field Password field container.
     * @return {boolean}
     */
    meetsRequirements(value, field) {
        // Declare and initialize
        let meetsLength   = value.length >= this.minimumLength;
        let meetsStrength = this.isStrong(field);

        return meetsLength && meetsStrength
    }

    /**
     * Validate the password and update the requirements status.
     *
     * @method validate
     * @since 2.0.0
     * @param {jQuery} password Password input.
     * @param {jQuery} field Password field container.
     * @param {jQuery} status Requirements status.
     * @return {boolean}
     */
    validate(password, field, status) {
        // Declare and initialize
        let input = password.get(0);
        let value = String(password.val() || '');

        // Bail if the password element is unavailable
        if (!input)
            return false;

        // Hide the status until the user begins typing
        if (!value.length) {

            // Clear the requirements status
            status
                .prop('hidden', true)
                .removeClass(
                    this.invalidClass + ' ' + this.validClass
                )
                .empty();

            // Clear our custom validation error
            input.setCustomValidity('');

            return false
        }

        // Determine whether the password meets the requirements
        let valid = this.meetsRequirements(value, field);

        // Handle an invalid password
        if (!valid) {

            // Set the browser validation message
            input.setCustomValidity(
                'Your password must contain at least ' +
                this.minimumLength +
                ' characters and have a strength rating of Strong.'
            );

            // Display the invalid status
            status
                .prop('hidden', false)
                .removeClass(this.validClass)
                .addClass(this.invalidClass)
                .html(
                    '<span class="password-requirements-status__icon" ' +
                    'aria-hidden="true">!</span>' +
                    '<span class="password-requirements-status__message">' +
                    'Your password needs to be at least ' +
                    this.minimumLength +
                    ' characters and ' +
                    '<span class="badge badge-success p-1">' +
                    'Strong' +
                    '</span>.' +
                    '</span>'
                );

            return false
        }

        // Clear our custom validation error
        input.setCustomValidity('');

        // Display the successful status
        status
            .prop('hidden', false)
            .removeClass(this.invalidClass)
            .addClass(this.validClass)
            .html(
                '<span class="password-requirements-status__icon" ' +
                'aria-hidden="true">&#10003;</span>' +
                '<span class="password-requirements-status__message">' +
                'Your password fulfills the requirements.' +
                '</span>'
            );

        return true
    }
}

export default PasswordRequirements;